import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { fetchIntegratedPlatform } from '../../integratedSlice';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from '@mui/material/Snackbar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    height: '100%'
}));

const CentricIntegration = ({ storeName, platformName, storeParam, storeId }) => {
    const dispatch = useDispatch();

    const profile = useSelector(state => state.auth);
    const [open, setOpen] = React.useState(false);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };


    const copyToken = () => {
        navigator.clipboard.writeText(profile.profile.api_token);
        setOpen(true);

    }
    const action = (
        <>
            <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleClose}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </>
    );
    return (
        <>
            <Grid container spacing={0} justifyContent="center" sx={{ my: 4 }}>
                <Grid item xs={12} sm={10} md={8} lg={6} style={{ textAlign: 'center' }}>
                    <Card
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: '16px',
                            border: '1px solid #E2E8F0',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
                            backgroundColor: '#FFFFFF'
                        }}
                    >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            <Item style={{ marginBottom: '0px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 3.5 }}>
                                    
                                    {/* Brand Identity Accent Block */}
                                    <Box sx={{ p: 2, backgroundColor: '#F8FAFC', borderRadius: '14px', border: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box
                                            component="img"
                                            src="/assets/images/integration/SHIPPINGCHIMP.png"
                                            alt="SHIPPINGCHIMP"
                                            sx={{ width: 'auto', height: '44px', objectFit: 'contain' }}
                                        />
                                    </Box>

                                    {/* Descriptive Typography Header Setup */}
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '18px', mb: 0.5, letterSpacing: '-0.3px' }}>
                                            Custom API Integration
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#64748B', maxWidth: '360px', mx: 'auto', lineHeight: 1.5 }}>
                                            Utilize your secure credential token endpoint below to map asynchronous checkout logs from customized developer channels.
                                        </Typography>
                                    </Box>

                                    {/* Read-Only Token Value Interactive Form Actions Row */}
                                    <Box sx={{ width: '100%' }}>
                                        <Box 
                                            className="sc--form--center" 
                                            sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                gap: 1.5,
                                                width: '100%',
                                                maxWidth: '480px',
                                                mx: 'auto'
                                            }}
                                        >
                                            <TextField 
                                                id="standard-basic" 
                                                size='small' 
                                                defaultValue={profile.profile.api_token} 
                                                InputProps={{
                                                    readOnly: true,
                                                }} 
                                                variant="outlined" 
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '10px',
                                                        backgroundColor: '#F8FAFC',
                                                        fontFamily: 'monospace',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        color: '#334155',
                                                        '& fieldset': { borderColor: '#E2E8F0' }
                                                    }
                                                }}
                                            />
                                            <Button 
                                                variant="contained" 
                                                onClick={copyToken}
                                                sx={{
                                                    height: '40px',
                                                    px: 3,
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: '13.5px',
                                                    boxShadow: 'none',
                                                    backgroundColor: '#1976d2',
                                                    width: { xs: '100%', sm: 'auto' },
                                                    '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Contextual Technical API Documentation Baseline Link */}
                                    <Box className='text-center' sx={{ pt: 2, borderTop: '1px solid #F1F5F9', width: '100%' }}>
                                        <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                                            <b>Marketplace API Documentation: </b> 
                                            <Box 
                                                component="a" 
                                                href="http://labelingchimp-docs.shippingchimp.com/#/" 
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ 
                                                    color: '#1976d2', 
                                                    textDecoration: 'none', 
                                                    fontWeight: 700,
                                                    ml: 0.5,
                                                    '&:hover': { textDecoration: 'underline' }
                                                }}
                                            >
                                                Refer Here
                                            </Box>
                                        </Typography>
                                    </Box>

                                    <Snackbar
                                        open={open}
                                        autoHideDuration={6000}
                                        onClose={handleClose}
                                        message="API Token has copied to the clipboard"
                                        action={action}
                                    />
                                </Box>
                            </Item>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </>
    )
}

export default CentricIntegration;